import { DATA_TEST } from '../enums';

export interface IMenuItemDimensions {
  dims: DOMRect;
  dataTest: string;
}

/**
 * Gets the widths of the nav container and utility tray for responsive calculations.
 * Used to determine if items need to be hidden or can be shown based on available space.
 */
export const getNavContainerWidths = (navContainerEl: HTMLDivElement) => {
  if (!navContainerEl) return;

  const navVisibleWidth = navContainerEl.clientWidth;
  const navOverflowWidth = navContainerEl.scrollWidth - navVisibleWidth;

  const utilityIconsContainer = navContainerEl.querySelector(`[data-test="${DATA_TEST.desktop_utility_tray_options}"]`);

  return {
    navVisibleWidth,
    navOverflowWidth,
    utilContainerWidth: utilityIconsContainer?.getBoundingClientRect().width
  };
};

/**
 * Calculates the available horizontal space between the last child element and the right edge of its container.
 * This is used to determine if there is room to restore hidden menu items when the navigation bar width increases.
 *
 * @param container - The container element to check for available space
 * @returns The available space in pixels between the last child and right edge of the container.
 *          Returns 0 if container is null.
 *          Returns container's full width if there are no child elements.
 */
export function getAvailableContainerSpace(container: HTMLDivElement): number {
  if (!container) return 0;

  // Get the container's right edge position
  const containerRight = container.getBoundingClientRect().right;

  // Find the last child element
  const lastChild = container.lastElementChild as HTMLElement;
  if (!lastChild) return container.clientWidth; // If no children, return full width

  // Get the right edge position of the last child
  const lastChildRight = lastChild.getBoundingClientRect().right;

  // Calculate the available space
  return Math.abs(containerRight - lastChildRight);
}

/**
 * Gets dimensions of currently visible menu items in the container.
 * Can optionally include or exclude the favorites menu item.
 */
export const getVisibleMenuItemDims = (menuItemsContainer: HTMLDivElement, includeFavorites: boolean = false) => {
  const dynamicMenuItems = Array.from(
    menuItemsContainer?.querySelectorAll(
      // Matches only direct child dynamic menu items, omitting favorites menu item if it exists
      `:scope > [data-test^='level_one_item']${includeFavorites ? '' : ':not([data-test="level_one_item_favorites"])'}`
    ) || []
  );
  return dynamicMenuItems.map((element) => {
    return {
      dataTest: element.getAttribute('data-test'),
      dims: element.getBoundingClientRect()
    };
  });
};

interface HandleNavOverflowingArgs {
  menuItemsContainer: HTMLDivElement;
  isUtilityTrayVisible: boolean;
  hideUtilityTray: () => void;
  triggerMobileBreakpoint: () => void;
  hideNextItem: (dims: IMenuItemDimensions) => void;
}

/**
 * Handles navigation overflow by progressively hiding items and utility tray.
 * If overflow persists after hiding all possible items, triggers mobile breakpoint.
 */
export const handleNavOverflowing = ({
  menuItemsContainer,
  isUtilityTrayVisible,
  hideNextItem,
  hideUtilityTray,
  triggerMobileBreakpoint
}: HandleNavOverflowingArgs) => {
  const visibleMenuItemDims = getVisibleMenuItemDims(menuItemsContainer, true);

  const areMaxItemsHidden = visibleMenuItemDims.length <= 1;

  // If we can still hide items, hide the next one
  if (!areMaxItemsHidden) {
    const nextHiddenItemDims = visibleMenuItemDims.slice(visibleMenuItemDims.length - 1);
    hideNextItem(nextHiddenItemDims[0]);
    return;
  }

  // Hide the utility tray to free up additional space after all hidable items are hidden
  if (isUtilityTrayVisible && areMaxItemsHidden) {
    hideUtilityTray();
    return;
  }

  // If overflow persists after all hidable items are hidden, trigger mobile breakpoint as a fallback
  if (!isUtilityTrayVisible && areMaxItemsHidden) {
    triggerMobileBreakpoint();
  }
};

interface HandleNavExpandingArgs {
  menuItemsContainer: HTMLDivElement;
  hiddenItemDims: IMenuItemDimensions[];
  hiddenUtilContainerWidth: number;
  isMoreMenuVisible: boolean;
  isUtilityTrayVisible: boolean;
  availableSpace: number;
  showUtilityTray: () => void;
  showNextHiddenItem: () => void;
  itemFlexGap?: number;
}

/**
 * Handles navigation expansion by progressively showing hidden items and utility tray
 * as more space becomes available. Considers the space needed for the "More" tab
 * when showing the last hidden item.
 */
export const evaluateHiddenItemRestoration = ({
  menuItemsContainer,
  hiddenItemDims,
  hiddenUtilContainerWidth,
  isMoreMenuVisible,
  isUtilityTrayVisible,
  availableSpace,
  showUtilityTray,
  showNextHiddenItem,
  itemFlexGap = 8
}: HandleNavExpandingArgs) => {
  // Get the actual width of the "More" menu tab
  const moreMenuTab = menuItemsContainer.querySelector(`[data-test="${DATA_TEST.more_top_level_menu_item}"]`);
  const moreTabWidth = moreMenuTab?.getBoundingClientRect().width ?? 60;

  // The first item must always be shown, so we only consider the space of the remaining visible items
  const removableItemDims = getVisibleMenuItemDims(menuItemsContainer, true).slice(1);
  const spaceOccupiedByRemovableItems = removableItemDims.reduce((acc, item) => acc + item.dims.width + itemFlexGap, 0);
  // Showing the utility tray will make the nav bar wider, which will cause menu items to be hidden
  // So we wait until there is enough space to offset the re-appearance of the utility tray before showing it
  if (spaceOccupiedByRemovableItems + availableSpace > hiddenUtilContainerWidth && !isUtilityTrayVisible) {
    showUtilityTray();
    return;
  }
  if (hiddenItemDims.length === 0) {
    return;
  }
  const nextItemToShow = hiddenItemDims[0];
  const isLastHiddenItem = hiddenItemDims.length === 1;

  // The "More" tab will disappear when the last hidden item is shown,
  // so we can add its width to the available space to offset the space it will free up
  const adjustedAvailableSpace = availableSpace + (isMoreMenuVisible && isLastHiddenItem ? moreTabWidth : 0);
  const nextItemToShowHasSpace = nextItemToShow.dims.width + itemFlexGap < adjustedAvailableSpace;

  // If there are items to restore, add the last hidden item
  if (nextItemToShowHasSpace) {
    showNextHiddenItem();
  }
};
